import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../../store";
import {
  Matchs,
  Table,
  BlockedMatchModalParams,
  OccupiedTableModalParams,
  FreedMatchModalParams,
  Players,
  BlockingPlayer,
  MatchWithBlockInfo,
  BlockedPlayer,
} from "../../types";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import firestoreInstance from "../../services/firestoreInstance";
import checkIsMatchLaunchable, {
  findMatchesToSetDoublonOnLaunch,
} from "./isMatchLaunchable";

export type MatchsState = {
  matchs: Matchs[];
  tables: Table[];
  blockedMatchModalParams: BlockedMatchModalParams | null;
  occupiedTableModalParams: OccupiedTableModalParams | null;
  freedMatchModalParams: FreedMatchModalParams | null;
  loading: boolean;
};

const initialState: MatchsState = {
  matchs: [],
  tables: [],
  blockedMatchModalParams: null,
  occupiedTableModalParams: null,
  freedMatchModalParams: null,
  loading: false,
};

// Fonction utilitaire pour trouver tous les chemins des champs undefined dans un objet
function findUndefinedPaths(obj: any, path: string = ""): string[] {
  let undefinedPaths: string[] = [];

  if (Array.isArray(obj)) {
    obj.forEach((item, idx) => {
      const currentPath = `${path}[${idx}]`;
      undefinedPaths = undefinedPaths.concat(
        findUndefinedPaths(item, currentPath)
      );
    });
  } else if (obj && typeof obj === "object") {
    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = path ? `${path}.${key}` : key;
      if (value === undefined) {
        undefinedPaths.push(currentPath);
      } else if (typeof value === "object" && value !== null) {
        undefinedPaths = undefinedPaths.concat(
          findUndefinedPaths(value, currentPath)
        );
      }
    });
  }

  return undefinedPaths;
}

// Fonction utilitaire pour nettoyer un joueur (retirer participation)
function cleanPlayer(player: any) {
  if (!player) return player;
  const { participation, ...rest } = player;
  return rest;
}

export const createMatch = createAsyncThunk<
  any,
  { match: Matchs },
  { state: RootState; dispatch: AppDispatch }
>("tournoi/createMatch", async ({ match }, { getState, dispatch }) => {
  const { isMatchLaunchable, blockedMatchModalParams } = checkIsMatchLaunchable(
    match,
    getState().matchs.matchs
  );
  if (!isMatchLaunchable) {
    match.status = "DOUBLON";
    for (const bp of blockedMatchModalParams.blockingPlayers) {
      await updateDoc(doc(firestoreInstance, "matchs", bp.match.id), {
        bloquant: true,
      });
    }
  }

  // Récupérer l'ID du tournoi actuel
  const currentTournamentId = getState().tournoi.tournoi?.id;
  if (!currentTournamentId) {
    throw new Error("Tournoi courant introuvable");
  }

  // Ajouter l'ID du tournoi au match
  match.tournoiId = currentTournamentId;

  match.tableau = getState().tournoi.boards.find(
    (board) => board.id === match.tableauId
  );
  match.tour = getState().tournoi.rounds.find(
    (round) => round.id === match.tourId
  );
  const playersWithRegistrations = getState().player.playersWithRegistrations;
  match.matchsPlayerParticipations = match.matchsPlayerParticipations.map(
    (mpp) => ({
      ...mpp,
      players: cleanPlayer(
        playersWithRegistrations.find((p) => p.id === mpp.playersId)
      ),
    })
  );

  try {
    console.log("match", match);
    console.log(JSON.stringify(match, null, 2));
    const undefinedPaths = findUndefinedPaths(match);
    console.log("Undefined paths in match:", undefinedPaths);
    await addDoc(collection(firestoreInstance, "matchs"), match);
  } catch (error) {
    console.error("Erreur lors de la création du match :", error);
    throw new Error("Erreur lors de la création du match");
  }

  return;
});

// Fonction utilitaire qui recalculera la libération des matchs doublons
const recalculateBlockedMatches = async (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const state = getState();

  // Obtenir tous les matchs ENCOURS actuels
  const liveMatches = state.matchs.matchs.filter((m) => m.status === "ENCOURS");

  // Tous les doublons encore présents
  const blockedMatchs = state.matchs.matchs.filter(
    (m) => m.status === "DOUBLON"
  );

  let freedMatchs: Matchs[] = [];
  let freedPlayers: Players[] = [];

  for (const doublon of blockedMatchs) {
    // Vérifier s'il reste un joueur bloquant
    const stillBlocked = doublon.matchsPlayerParticipations.some((mpp) => {
      // ce joueur est-il dans un match ENCOURS différent ?
      return liveMatches.some(
        (liveMatch) =>
          liveMatch.id !== doublon.id &&
          liveMatch.matchsPlayerParticipations.some(
            (liveMpp) => liveMpp.playersId === mpp.playersId
          )
      );
    });

    if (!stillBlocked) {
      // Le doublon peut être libéré
      await updateDoc(doc(firestoreInstance, "matchs", doublon.id), {
        status: "LIBERE",
        bloquant: false,
      });
      freedMatchs.push(doublon);
      // On ajoute les joueurs de ce doublon comme libérés (optionnel)
      freedPlayers.push(
        ...doublon.matchsPlayerParticipations
          .map((mpp) => mpp.players!)
          .filter(Boolean)
      );
    }
  }

  if (freedMatchs.length > 0) {
    dispatch(launchFreedMatchModal({ freedMatchs, freedPlayers }));
  }
};

const matchsSlice = createSlice({
  name: "matchs",
  initialState,
  reducers: {
    setMatchs: (state, action: PayloadAction<Matchs[]>) => {
      state.matchs = action.payload;
      state.loading = false;
    },
    setMatchsLoading: (state) => {
      state.loading = true;
    },
    setTables: (state, action: PayloadAction<Table[]>) => {
      state.tables = action.payload;
    },
    launchBlockedMatchModal: (
      state,
      action: PayloadAction<BlockedMatchModalParams>
    ) => {
      state.blockedMatchModalParams = action.payload;
    },
    closeBlockedMatchModal: (state) => {
      state.blockedMatchModalParams = null;
    },
    launchOccupiedTableModal: (
      state,
      action: PayloadAction<OccupiedTableModalParams>
    ) => {
      state.occupiedTableModalParams = action.payload;
    },
    closeOccupiedTableModal: (state) => {
      state.occupiedTableModalParams = null;
    },
    launchFreedMatchModal: (
      state,
      action: PayloadAction<FreedMatchModalParams>
    ) => {
      state.freedMatchModalParams = action.payload;
    },
    closeFreedMatchModal: (state) => {
      state.freedMatchModalParams = null;
    },
  },
});

export const {
  setMatchs,
  setMatchsLoading,
  setTables,
  launchBlockedMatchModal,
  closeBlockedMatchModal,
  launchOccupiedTableModal,
  closeOccupiedTableModal,
  launchFreedMatchModal,
  closeFreedMatchModal,
} = matchsSlice.actions;

// 🔥 Listen en temps réel aux matchs
export const listenToMatchs = () => (dispatch: AppDispatch) => {
  console.log("🔍 [DEBUG] Début de listenToMatchs");
  dispatch(setMatchsLoading());

  try {
    const q = query(
      collection(firestoreInstance, "matchs"),
      where("status", "!=", "TERMINE"),
      orderBy("dateCreation")
    );
    console.log("🔍 [DEBUG] Requête matchs créée, démarrage onSnapshot");

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log(
          "🔍 [DEBUG] onSnapshot matchs reçu, docs:",
          snapshot.docs.length
        );
        const matchs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          dateCreation: doc.data().dateCreation?.toDate(),
          dateLancement: doc.data().dateLancement?.toDate(),
          dateFin: doc.data().dateFin?.toDate(),
        })) as Matchs[];
        dispatch(setMatchs(matchs));
      },
      (error) => {
        console.error("❌ [DEBUG] Erreur onSnapshot matchs:", error);
      }
    );

    console.log("🔍 [DEBUG] listenToMatchs terminé avec succès");
    return unsubscribe;
  } catch (error) {
    console.error("❌ [DEBUG] Erreur dans listenToMatchs:", error);
    throw error;
  }
};

// 🔥 Chargement initial des tables
export const fetchTables = () => async (dispatch: AppDispatch) => {
  console.log("🔍 [DEBUG] Début de fetchTables");

  try {
    const snapshot = await getDocs(collection(firestoreInstance, "table"));
    console.log("🔍 [DEBUG] fetchTables réussi, docs:", snapshot.docs.length);

    const tables = snapshot.docs.map((doc) => ({
      ...doc.data(),
      tableId: parseInt(doc.id),
    })) as Table[];
    dispatch(setTables(tables));
  } catch (error) {
    console.error("❌ [DEBUG] Erreur dans fetchTables:", error);
    throw error;
  }
};

// 🔥 Change le statut d'un match (TERMINER, ENCOURS, etc)
export const changeMatchStatus =
  (match: Matchs) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (match.status === "ENCOURS") {
      // Passage d'un match ENCOURS à TERMINE
      await updateDoc(doc(firestoreInstance, "matchs", match.id), {
        status: "TERMINE",
        dateFin: new Date(),
      });

      // Recalcul libération des doublons
      await recalculateBlockedMatches(dispatch, getState);
    } else {
      // Passage d'un match CREE (ou autre) à ENCOURS

      const { isMatchLaunchable, blockedMatchModalParams } =
        checkIsMatchLaunchable(match, getState().matchs.matchs);

      const matchsOnTable = getState().matchs.matchs.filter(
        (m) =>
          m.status === "ENCOURS" && m.tableId === match.tableId && m.tableId
      );

      if (isMatchLaunchable && matchsOnTable.length === 0) {
        // 1) Mise à jour du match courant à ENCOURS
        await updateDoc(doc(firestoreInstance, "matchs", match.id), {
          status: "ENCOURS",
          dateLancement: new Date(),
          bloquant: false,
        });

        // 2) Recherche des autres matchs CREE qui partagent au moins un joueur avec ce match
        const state = getState();
        const matchsToBlock = findMatchesToSetDoublonOnLaunch(
          match,
          state.matchs.matchs
        );

        // 3) Mettre à jour ces matchs en DOUBLON + bloquant: true
        for (const mToBlock of matchsToBlock) {
          await updateDoc(doc(firestoreInstance, "matchs", mToBlock.id), {
            status: "DOUBLON",
          });
        }

        // Optionnel : on peut déclencher un modal pour avertir
        if (matchsToBlock.length > 0) {
          await updateDoc(doc(firestoreInstance, "matchs", match.id), {
            bloquant: true,
          });

          dispatch(
            launchBlockedMatchModal({
              blockingPlayers: matchsToBlock.map((m) => ({
                player: { id: "", name: "", surname: "" } as Players,
                match: m,
              })),
            })
          );
        }
      } else if (isMatchLaunchable && matchsOnTable.length !== 0) {
        // La table est occupée
        dispatch(
          launchOccupiedTableModal({
            blockedTable: match.tableId!,
            blockingMatchs: matchsOnTable,
          })
        );
      } else if (blockedMatchModalParams.blockingPlayers.length !== 0) {
        for (const bp of blockedMatchModalParams.blockingPlayers) {
          await updateDoc(doc(firestoreInstance, "matchs", bp.match.id), {
            bloquant: true,
          });
        }
        await updateDoc(doc(firestoreInstance, "matchs", match.id), {
          status: "DOUBLON",
        });
        dispatch(launchBlockedMatchModal(blockedMatchModalParams));
      }
    }
  };

export const forceMatchEnd = createAsyncThunk<
  void,
  { matchId: string },
  { state: RootState; dispatch: AppDispatch }
>("matchs/forceMatchEnd", async ({ matchId }, { dispatch }) => {
  await updateDoc(doc(firestoreInstance, "matchs", matchId), {
    status: "TERMINE",
    dateFin: new Date(),
  });
});

// 🔥 Changement de table
export const changeMatchTable =
  ({ matchId, tableId }: { matchId: string; tableId: number }) =>
  async (dispatch: AppDispatch) => {
    await updateDoc(doc(firestoreInstance, "matchs", matchId), { tableId });
  };

// 🔥 Selectors
export const getMatchs = (state: RootState) => state.matchs.matchs;
export const getTables = (state: RootState) => state.matchs.tables;
export const getMatchsLoading = (state: RootState) => state.matchs.loading;
export const getBlockedMatchModalParams = (state: RootState) =>
  state.matchs.blockedMatchModalParams;
export const getOccupiedTableModalParams = (state: RootState) =>
  state.matchs.occupiedTableModalParams;
export const getFreedMatchModalParams = (state: RootState) =>
  state.matchs.freedMatchModalParams;

const getLiveMatchs = (state: RootState) =>
  state.matchs.matchs.filter((match) => match.status === "ENCOURS");

const getAllTables = (state: RootState) => state.matchs.tables;
const getRoomId = (_state: RootState, roomId: number) => roomId;
export const getTablesWithMatchsGroupedByRows = createSelector(
  [getLiveMatchs, getRoomId, getAllTables],
  (matchs, roomId, tables) => {
    // Filtrer uniquement les tables définies et valides avec un roomId
    const tablesFromRoom = tables.filter(
      (table): table is Table =>
        table !== undefined &&
        table !== null &&
        typeof table.roomId !== "undefined" &&
        table.roomId === roomId
    );

    const tablesWithMatchs = tablesFromRoom.map((table) => {
      const matchsOnTable = matchs.filter(
        (match) => match.tableId === table.tableId
      );
      return { ...table, matchs: matchsOnTable };
    });

    const tablesGroupedByRows: { [row: number]: Table[] } = {};
    tablesWithMatchs.forEach((table) => {
      // On vérifie que row est défini avant de l'utiliser
      if (table.row !== undefined && table.row !== null) {
        if (!tablesGroupedByRows[table.row]) {
          tablesGroupedByRows[table.row] = [];
        }
        tablesGroupedByRows[table.row].push(table);
      }
    });

    // Tri par colonne dans chaque rangée
    Object.values(tablesGroupedByRows).forEach((rowTables) => {
      rowTables.sort((a, b) => (a.column > b.column ? 1 : -1));
    });

    return tablesGroupedByRows;
  }
);

export const getAvailableRooms = createSelector(
  [getLiveMatchs, getAllTables],
  (matchs, tables) => {
    const matchsWithTable = matchs.map((match) => {
      const table = tables.find((table) => table.tableId === match.tableId);
      return { ...match, table };
    });

    const roomIdsDistincts: number[] = [];
    matchsWithTable.forEach((match) => {
      if (match.table && typeof match.table.roomId === "number") {
        const roomId = match.table.roomId;
        if (!roomIdsDistincts.includes(roomId)) {
          roomIdsDistincts.push(roomId);
        }
      }
    });

    if (roomIdsDistincts.length === 0) {
      roomIdsDistincts.push(1);
    }

    return roomIdsDistincts.sort((a, b) => a - b);
  }
);

export const getAvailableRoomNames = createSelector(
  [getLiveMatchs, getAllTables],
  (matchs, tables) => {
    const matchsWithTable = matchs.map((match) => {
      const table = tables.find((table) => table.tableId === match.tableId);
      return { ...match, table };
    });

    const roomNamesDistincts: string[] = [];
    matchsWithTable.forEach((match) => {
      if (match.table && match.table.roomName) {
        const roomName = match.table.roomName;
        if (!roomNamesDistincts.includes(roomName)) {
          roomNamesDistincts.push(roomName);
        }
      }
    });

    if (roomNamesDistincts.length === 0) {
      roomNamesDistincts.push("Salle 1");
    }

    return roomNamesDistincts.sort((a, b) => a.localeCompare(b));
  }
);

export const getPlayersWithCurrentMatchs = (state: RootState): Players[] => {
  const players: Players[] = [];

  state.matchs.matchs
    .filter((match) => match.status === "ENCOURS")
    .forEach((match) => {
      match.matchsPlayerParticipations.forEach((mpp) => {
        if (mpp.players) {
          const matchPlayerParticipation = { ...mpp, match };
          const player = {
            ...mpp.players,
            matchPlayerParticipation: [matchPlayerParticipation],
          };
          players.push(player);
        }
      });
    });

  return players.sort((p1, p2) => p1.name.localeCompare(p2.name));
};

export const selectMatchesWithBlockInfo = createSelector(
  (state: RootState) => state.matchs.matchs,
  (matches): MatchWithBlockInfo[] => {
    // Filtrer les matchs non terminés pour optimiser
    const activeMatches = matches.filter((m) => m.status !== "TERMINE");

    return activeMatches.map((match) => {
      let blockedByPlayers: BlockingPlayer[] = [];
      let blockedPlayers: BlockedPlayer[] = [];

      if (match.status === "DOUBLON") {
        // Trouver les matchs ENCOURS qui bloquent ce match DOUBLON
        blockedByPlayers = activeMatches
          .filter((m) => m.status === "ENCOURS" && m.bloquant)
          .flatMap((m) => {
            // Récupérer les joueurs communs aux deux matchs
            const blockingPlayerIds = new Set(
              m.matchsPlayerParticipations.map((mpp) => mpp.playersId)
            );
            const isBlocking = match.matchsPlayerParticipations.some((mp) =>
              blockingPlayerIds.has(mp.playersId)
            );

            if (!isBlocking) return [];
            // Ne garder que les joueurs communs (bloquants)
            return match.matchsPlayerParticipations
              .filter((mpp) => blockingPlayerIds.has(mpp.playersId))
              .map((mpp) => ({
                player: mpp.players!,
                match: m,
              }));
          });
      } else if (match.status === "ENCOURS" && match.bloquant) {
        // Trouver les matchs bloqués par ce match ENCOURS
        const blockedMatches = activeMatches.filter((m) => {
          if (m.id === match.id) return false;
          if (m.status === "TERMINE") return false;
          if (m.status === "ENCOURS") return false;

          // Vérifie s'il y a joueur commun (joueur bloqué)
          return m.matchsPlayerParticipations.some((mpp) =>
            match.matchsPlayerParticipations.some(
              (mp) => mp.playersId === mpp.playersId
            )
          );
        });

        // Pour chacun, remonter uniquement les joueurs bloqués communs aux deux matchs
        blockedPlayers = blockedMatches.flatMap((bm) => {
          const blockingPlayerIds = new Set(
            match.matchsPlayerParticipations.map((mpp) => mpp.playersId)
          );

          return bm.matchsPlayerParticipations
            .filter((mpp) => blockingPlayerIds.has(mpp.playersId))
            .map((mpp) => ({
              player: mpp.players!,
              match: bm,
            }));
        });
      }

      return {
        ...match,
        blockedByPlayers:
          blockedByPlayers.length > 0 ? blockedByPlayers : undefined,
        blockedPlayers: blockedPlayers.length > 0 ? blockedPlayers : undefined,
      };
    });
  }
);

export default matchsSlice.reducer;
